import { useMemo, useState } from 'react';
import {
  Flex,
  IconButton,
  Icon,
  Heading,
  Select,
  SelectMultiple,
  Text,
  Spinner,
  SegmentedControl,
  TextField,
  Badge,
  Tooltip,
} from '@scale/scaleui-radix';
import { faPlus, faGhost } from '@fortawesome/pro-regular-svg-icons';
import { AuditRubricCriterion } from '@scale/shared-types';
import { useUser } from 'react-scale/components/ui/UserContext';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useAuditErrorCategories } from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import { AddErrorCategoryModal } from './AddErrorCategoryModal';

const clearSelectionValue = '__clear__';

export const FillCriterionErrorCategories = ({
  projectId,
  errorCategories,
  selectedErrorCategory,
  setSelectedErrorCategory,
}: {
  projectId: string;
  errorCategories: string[];
  selectedErrorCategory?: string;
  setSelectedErrorCategory: (errorCategory?: string) => void;
}) => {
  const displayStandardizedErrorCategories = useUnleashFeatureFlag(
    'enable-standardized-error-categories',
    { projectId },
    false,
  );
  const { data: allErrorCategories, isFetching: isFetchingErrorCategories } =
    useAuditErrorCategories(projectId, displayStandardizedErrorCategories);
  const allErrorCategoriesSet = useMemo(
    () => new Set(allErrorCategories ?? []),
    [allErrorCategories],
  );
  const errorCategoryOptions = useMemo(() => {
    return errorCategories.filter(category => allErrorCategoriesSet?.has(category));
  }, [errorCategories, allErrorCategoriesSet]);
  return (
    <Flex direction="column" gap="2">
      <Text weight="medium">Error Categories</Text>
      {isFetchingErrorCategories ? (
        <Spinner />
      ) : errorCategoryOptions.length > 0 ? (
        <Select.Root
          value={selectedErrorCategory ?? ''}
          onValueChange={value =>
            setSelectedErrorCategory(value === clearSelectionValue ? undefined : value)
          }
        >
          <Select.Trigger className="w-full" />
          <Select.Content>
            <Select.Item value={clearSelectionValue}>
              <Text color="neutral">Clear selection</Text>
            </Select.Item>
            <Select.Separator />
            {errorCategoryOptions.map(errorCategory => (
              <Select.Item key={errorCategory} value={errorCategory}>
                {errorCategory}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      ) : (
        <Text size="1" color="neutral">
          You have no error categories set for this criterion.
        </Text>
      )}
    </Flex>
  );
};

const ViewCriterionErrorCategories = ({
  criterion,
  allErrorCategories,
  errorCategoriesError,
  isFetchingErrorCategories,
}: {
  criterion: AuditRubricCriterion;
  allErrorCategories?: string[];
  errorCategoriesError: Error | null;
  isFetchingErrorCategories: boolean;
}) => {
  const selectedErrorCategories = useMemo(() => {
    return criterion.errorCategories.filter(category => allErrorCategories?.includes(category));
  }, [criterion.errorCategories, allErrorCategories]);
  const staleErrorCategories = useMemo(() => {
    return criterion.useAllErrorCategories
      ? []
      : criterion.errorCategories.filter(category => !allErrorCategories?.includes(category));
  }, [criterion.errorCategories, allErrorCategories, criterion.useAllErrorCategories]);
  const errorCategoriesText = criterion.useAllErrorCategories
    ? ['All categories']
    : selectedErrorCategories.length > 0
    ? selectedErrorCategories
    : ['None'];
  return (
    <Flex direction="column" gap="1" width="100%">
      <Text color="neutral">Error Categories</Text>
      {errorCategoriesError?.message ? (
        <Text size="1" color="neutral">
          {errorCategoriesError.message ?? 'Error loading error categories'}
        </Text>
      ) : isFetchingErrorCategories ? (
        <Spinner />
      ) : (
        <Flex direction="row" gap="2" wrap="wrap">
          <>
            {errorCategoriesText.map(errorCategory => (
              <Badge key={errorCategory} color="neutral">
                {errorCategory}
              </Badge>
            ))}
            {staleErrorCategories.length > 0 && (
              <Tooltip content={`Some categories are stale: ${staleErrorCategories.join(', ')}`}>
                <Icon icon={faGhost} label="Some categories are stale" color="error" />
              </Tooltip>
            )}
          </>
        </Flex>
      )}
    </Flex>
  );
};

const EditCriterionErrorCategories = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  allErrorCategories,
  errorCategoriesError,
  isFetchingErrorCategories,
  projectId,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  isEditingCriterion: boolean;
  allErrorCategories?: string[];
  errorCategoriesError: Error | null;
  isFetchingErrorCategories: boolean;
  projectId: string;
  error?: string;
}) => {
  const setUseAllErrorCategories = (useAllErrorCategories: boolean) => {
    setCriterion({ ...criterion, useAllErrorCategories });
  };

  const setSelectedErrorCategories = (errorCategories: string[]) => {
    setCriterion({ ...criterion, errorCategories });
  };
  const addErrorCategory = (errorCategory: string) => {
    setSelectedErrorCategories([...criterion.errorCategories, errorCategory]);
  };

  const allErrorCategoriesSet = useMemo(
    () => new Set(allErrorCategories ?? []),
    [allErrorCategories],
  );

  const [searchQuery, setSearchQuery] = useState('');

  const filteredErrorCategories = useMemo(() => {
    if (!allErrorCategories) return [];
    if (!searchQuery.trim()) return allErrorCategories;
    return allErrorCategories.filter(category =>
      category.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [allErrorCategories, searchQuery]);

  const [errorCategoryModalOpen, setErrorCategoryModalOpen] = useState<boolean>(false);
  const user = useUser();

  const selectedErrorCategories = useMemo(() => {
    return criterion.errorCategories.filter(category => allErrorCategoriesSet?.has(category));
  }, [criterion.errorCategories, allErrorCategoriesSet]);
  const selectedErrorCategorySet = useMemo(() => {
    return new Set(selectedErrorCategories);
  }, [selectedErrorCategories]);
  const staleErrorCategories = useMemo(() => {
    return criterion.errorCategories.filter(category => !allErrorCategoriesSet?.has(category));
  }, [criterion.errorCategories, allErrorCategoriesSet]);

  const editingErrorCategoriesSelected = (
    <>
      <Text size="1" color="neutral">
        Selected categories are applied to the task if the score falls within their configured
        range.
      </Text>
      {errorCategoriesError?.message ? (
        <Text size="1" color="neutral">
          {errorCategoriesError.message ?? 'Error loading error categories'}
        </Text>
      ) : isFetchingErrorCategories ? (
        <Spinner />
      ) : allErrorCategories && allErrorCategories.length > 0 ? (
        <Flex direction="row" gap="2" width="100%">
          <SelectMultiple.Root
            value={selectedErrorCategories}
            onValueChange={(errorCategories: string[]) =>
              setSelectedErrorCategories([...errorCategories, ...staleErrorCategories])
            }
          >
            <SelectMultiple.Trigger className="w-full" />
            <SelectMultiple.Content>
              <Flex direction="row" gap="2" align="center" width="100%">
                <TextField.Root
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  style={{ flex: 1 }}
                />
                <IconButton
                  variant="ghost"
                  size="1"
                  color="neutral"
                  onClick={() => setErrorCategoryModalOpen(true)}
                  disabled={!user}
                >
                  <Icon label="Add category" icon={faPlus} />
                </IconButton>
              </Flex>
              {filteredErrorCategories.length > 0 ? (
                [
                  ...selectedErrorCategories.map(errorCategory => (
                    <SelectMultiple.Item key={errorCategory} value={errorCategory}>
                      {errorCategory}
                    </SelectMultiple.Item>
                  )),
                  ...filteredErrorCategories
                    .filter(category => !selectedErrorCategorySet.has(category))
                    .map(errorCategory => (
                      <SelectMultiple.Item key={errorCategory} value={errorCategory}>
                        {errorCategory}
                      </SelectMultiple.Item>
                    )),
                ]
              ) : (
                <Text size="1" color="neutral">
                  No categories match your search.
                </Text>
              )}
            </SelectMultiple.Content>
          </SelectMultiple.Root>
        </Flex>
      ) : (
        <Text size="1" color="neutral">
          You have no error categories configured for this project.
        </Text>
      )}
      {!isFetchingErrorCategories && staleErrorCategories.length > 0 ? (
        <Flex direction="row" gap="2" align="center" width="100%" wrap="wrap">
          <Text size="1" color="neutral">
            Stale categories:
          </Text>
          {staleErrorCategories.map(category => (
            <Flex direction="row" gap="2" align="center" key={category}>
              <Badge color="neutral" variant="outline" radius="full" size="1">
                {category}
              </Badge>
            </Flex>
          ))}
        </Flex>
      ) : null}
    </>
  );

  const editingErrorCategoriesAll = (
    <>
      <Text size="1" color="neutral">
        All project-configured error categories are applied to the task if the score falls within
        their configured range.
      </Text>
    </>
  );

  return (
    <Flex
      direction={isEditingCriterion ? 'column' : 'row'}
      gap="2"
      align={isEditingCriterion ? 'start' : 'center'}
      width="100%"
    >
      <Heading size="2">Error categories</Heading>
      <SegmentedControl.Root
        value={
          criterion.useAllErrorCategories ? 'all-error-categories' : 'selected-error-categories'
        }
        style={{ width: '100%' }}
      >
        <SegmentedControl.Item
          value="all-error-categories"
          onClick={() => setUseAllErrorCategories(true)}
        >
          All categories
        </SegmentedControl.Item>
        <SegmentedControl.Item
          value="selected-error-categories"
          onClick={() => setUseAllErrorCategories(false)}
        >
          Selected categories
        </SegmentedControl.Item>
      </SegmentedControl.Root>
      {criterion.useAllErrorCategories ? editingErrorCategoriesAll : editingErrorCategoriesSelected}
      {error && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}
      <AddErrorCategoryModal
        open={errorCategoryModalOpen}
        setOpen={setErrorCategoryModalOpen}
        addErrorCategory={addErrorCategory}
        projectId={projectId}
        userEmail={user?.email ?? ''}
      />
    </Flex>
  );
};

export const CriterionErrorCategories = ({
  projectId,
  criterion,
  setCriterion,
  allErrorCategories,
  errorCategoriesError,
  isFetchingErrorCategories,
  isEditingCriterion,
  error,
}: {
  projectId: string;
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  allErrorCategories: string[];
  errorCategoriesError: Error | null;
  isFetchingErrorCategories: boolean;
  isEditingCriterion: boolean;
  error?: string;
}) => {
  return isEditingCriterion ? (
    <EditCriterionErrorCategories
      criterion={criterion}
      setCriterion={setCriterion}
      isEditingCriterion={isEditingCriterion}
      allErrorCategories={allErrorCategories}
      errorCategoriesError={errorCategoriesError}
      isFetchingErrorCategories={isFetchingErrorCategories}
      error={error}
      projectId={projectId}
    />
  ) : (
    <ViewCriterionErrorCategories
      criterion={criterion}
      allErrorCategories={allErrorCategories}
      errorCategoriesError={errorCategoriesError}
      isFetchingErrorCategories={isFetchingErrorCategories}
    />
  );
};
